/**
 * Configuración de Content Security Policy (CSP)
 * Previene ataques XSS y mejora la seguridad general
 * Usa variables de entorno para hacer la configuración dinámica
 */

/**
 * Extrae la URL base del API desde REACT_APP_API_URL
 * @returns {string|null} - URL base del API sin el path /api
 */
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) return null;
  
  try {
    const url = new URL(apiUrl);
    // Remover /api del path si existe
    const baseUrl = `${url.protocol}//${url.host}`;
    return baseUrl;
  } catch (error) {
    return null;
  }
};

/**
 * Obtiene la URL de Supabase desde variables de entorno
 * @returns {string|null} - URL de Supabase
 */
const getSupabaseUrl = () => {
  return process.env.REACT_APP_SUPABASE_URL || null;
};

/**
 * Genera la política CSP para la aplicación
 * @param {boolean} isDevelopment - Si está en modo desarrollo
 * @returns {string} - Política CSP
 */
export const generateCSP = (isDevelopment = false) => {
  // Obtener URLs dinámicamente desde variables de entorno
  const apiBaseUrl = getApiBaseUrl();
  const supabaseUrl = getSupabaseUrl();
  
  // Construir connect-src dinámicamente
  const connectSrc = ["'self'"];
  
  // Agregar URL del backend API si está disponible
  if (apiBaseUrl) {
    connectSrc.push(apiBaseUrl);
  } else if (isDevelopment) {
    // Fallback a localhost solo en desarrollo si no hay variable
    connectSrc.push('http://localhost:4000');
  }
  
  // Agregar URL de Supabase si está disponible
  if (supabaseUrl) {
    connectSrc.push(supabaseUrl);
  }
  
  // Servicios externos estáticos (siempre permitidos)
  connectSrc.push('https://api.dicebear.com');
  connectSrc.push('https://images.unsplash.com');
  
  // Agregar Supabase Storage para imágenes/logos si está disponible
  if (supabaseUrl) {
    // Extraer el dominio base de Supabase para Storage
    try {
      const supabaseDomain = new URL(supabaseUrl).hostname;
      // Supabase Storage usa el mismo dominio pero con storage
      connectSrc.push(`https://${supabaseDomain}`);
      // También agregar para img-src
    } catch (error) {
      // Ignorar si hay error al parsear
    }
  }
  
  const basePolicy = {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      ...(isDevelopment ? ["'unsafe-inline'", "'unsafe-eval'"] : []), // Solo en desarrollo
      'https://unpkg.com', // Para librerías externas
      'https://cdn.jsdelivr.net',
      'https://cdn.tailwindcss.com'
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Necesario para Tailwind CSS
      'https://fonts.googleapis.com'
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'data:'
    ],
    'img-src': [
      "'self'",
      'data:',
      'blob:',
      'https://api.dicebear.com',
      'https://images.unsplash.com',
      'https://via.placeholder.com',
      // Agregar Supabase Storage para imágenes/logos
      // Extraer dominio de Supabase si está disponible
      ...(supabaseUrl ? (() => {
        try {
          const supabaseHostname = new URL(supabaseUrl).hostname;
          // El dominio específico de Supabase (ej: tycoujgrgwlaukiqqqim.supabase.co)
          // Esto permite cargar imágenes desde Supabase Storage
          return [`https://${supabaseHostname}`];
        } catch (error) {
          return [];
        }
      })() : []),
      // IMPORTANTE: Los wildcards como *.supabase.co NO funcionan en CSP
      // Si no hay REACT_APP_SUPABASE_URL configurada, el dominio específico no se agregará
      // La solución es configurar REACT_APP_SUPABASE_URL en Vercel
    ].filter(Boolean), // Remover valores undefined/null
    'connect-src': connectSrc,
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    // 'frame-ancestors' no funciona en meta tags, solo en headers HTTP
    // Se debe configurar en el servidor (Vercel headers)
    'upgrade-insecure-requests': [],
    'block-all-mixed-content': []
  };

  // Convertir a string CSP
  return Object.entries(basePolicy)
    .map(([directive, sources]) => {
      if (sources.length === 0) {
        return directive;
      }
      return `${directive} ${sources.join(' ')}`;
    })
    .join('; ');
};

/**
 * Aplica la política CSP al documento
 * @param {boolean} isDevelopment - Si está en modo desarrollo
 */
export const applyCSP = (isDevelopment = false) => {
  if (typeof document === 'undefined') return;

  const csp = generateCSP(isDevelopment);
  
  // Crear meta tag para CSP
  const meta = document.createElement('meta');
  meta.httpEquiv = 'Content-Security-Policy';
  meta.content = csp;
  
  // Remover CSP existente si existe
  const existingCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (existingCSP) {
    existingCSP.remove();
  }
  
  // Agregar nueva CSP
  document.head.appendChild(meta);
};

/**
 * Configuración de CSP para diferentes entornos (legacy - usar generateCSP)
 * @deprecated Usar generateCSP() en su lugar que usa variables de entorno
 */
export const CSP_CONFIGS = {
  development: {
    'default-src': ["'self'"],
    'script-src': ["'self'", "'unsafe-inline'", "'unsafe-eval'"],
    'style-src': ["'self'", "'unsafe-inline'"],
    'img-src': ["'self'", "data:", "blob:", "https:"],
    'connect-src': ["'self'", "http://localhost:*", "https:"],
    'font-src': ["'self'", "data:", "https:"]
  },
  
  production: {
    'default-src': ["'self'"],
    'script-src': ["'self'", "https://unpkg.com", "https://cdn.jsdelivr.net"],
    'style-src': ["'self'", "https://fonts.googleapis.com"],
    'img-src': ["'self'", "data:", "blob:", "https://api.dicebear.com", "https://images.unsplash.com"],
    'connect-src': ["'self'"], // Se completa dinámicamente desde variables de entorno
    'font-src': ["'self'", "https://fonts.gstatic.com", "data:"],
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    // 'frame-ancestors' no funciona en meta tags, solo en headers HTTP
    'upgrade-insecure-requests': [],
    'block-all-mixed-content': []
  }
};

export default { generateCSP, applyCSP, CSP_CONFIGS };